/**
 * The English dictionary, and the source of truth for what keys exist.
 *
 * `as const` is what makes that true: `TranslationKey` is derived from these keys,
 * and every other language is typed as `Record<TranslationKey, string>`, so a
 * missing or misspelled translation fails `tsc` rather than falling back silently
 * at runtime in front of a user.
 *
 * Placeholders are `{name}` and are substituted by `translate()`.
 */
export const en = {
  // ─── Shared ────────────────────────────────────────────────────────────────
  'common.appName': 'AI Question Generator',
  'common.appTagline': 'Bangladesh SSC / HSC Curriculum',
  'common.brand': 'Tutor AI',
  'common.back': 'Back',
  'common.dashboard': 'Dashboard',
  'common.previous': 'Previous',
  'common.continue': 'Continue',
  'common.loading': 'Loading…',
  'common.processing': 'Processing…',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.edit': 'Edit',
  'common.done': 'Done',
  'common.view': 'View',
  'common.prev': 'Prev',
  'common.next': 'Next',
  'common.signIn': 'Sign In',
  'common.signOut': 'Sign out',
  'common.expired': 'Expired',
  'common.written': 'Written',
  'common.mcq': 'MCQ',
  'common.questions': 'questions',
  'common.minutesShort': 'min',
  'common.emptyValue': '—',

  // ─── Language toggle ───────────────────────────────────────────────────────
  'language.switchTo': 'Switch to {language}',

  // ─── Departments (dashboard analytics rows) ────────────────────────────────
  'dept.science': 'Science',
  'dept.commerce': 'Commerce',
  'dept.arts': 'Arts & Humanities',

  // ─── Payment / subscription statuses ───────────────────────────────────────
  'status.pending': 'Pending',
  'status.completed': 'Completed',
  'status.failed': 'Failed',
  'status.cancelled': 'Cancelled',
  'status.active': 'Active',
  'status.expired': 'Expired',

  // ─── Login ─────────────────────────────────────────────────────────────────
  'login.title': 'Question Paper Generator',
  'login.subtitle': 'AI-powered SSC & HSC exam paper generator',
  'login.welcome': 'Welcome back',
  'login.description': 'Sign in to generate exam papers and manage your subscription.',
  'login.google': 'Continue with Google',
  'login.terms':
    'By signing in you agree to our Terms of Service. New users get a 14-day free trial.',
  'login.suspendedTitle': 'This account is suspended',
  'login.suspendedBody':
    'Sign-in is blocked while the suspension is in force. Contact support if you believe this is a mistake.',
  'login.failedTitle': 'Sign-in failed',

  // ─── Session expiry ────────────────────────────────────────────────────────
  'session.title': 'Your session has expired',
  'session.description':
    'You have been signed out for security. Log in again to carry on — anything you have not generated yet will need to be redone.',
  'session.logout': 'Log out',

  // ─── Account suspension ────────────────────────────────────────────────────
  'suspended.title': 'Your account is suspended',
  'suspended.description':
    'An administrator has suspended this account, so it cannot be used until the suspension ends. Signing in again will not restore access.',
  'suspended.descriptionIndefinite':
    'An administrator has suspended this account indefinitely. Signing in again will not restore access — contact support if you believe this is a mistake.',
  'suspended.reasonLabel': 'Reason',
  'suspended.untilLabel': 'Suspended until',
  'suspended.indefinite': 'Indefinitely',
  'suspended.logout': 'Log out',

  // ─── Dashboard navigation ──────────────────────────────────────────────────
  'nav.analytics': 'Analytics',
  'nav.content': 'Content',
  'nav.account': 'Account',
  'nav.overview': 'Overview',
  'nav.history': 'History',
  'nav.subscription': 'Subscription',
  'nav.payments': 'Payment History',
  'nav.leaderboard': 'Leaderboard',
  'nav.admin': 'Administration',
  'nav.adminUsers': 'Users',
  'nav.adminActivity': 'User Activity',
  'nav.adminStats': 'Statistics',
  'nav.collapse': 'Collapse panel',
  'nav.resize': 'Resize panel — use arrow keys',
  'nav.dragResize': 'Drag to resize',
  'nav.breadcrumb': 'Home / Dashboard',

  // ─── Dashboard: hero + stats ───────────────────────────────────────────────
  'dashboard.newGeneration': 'New Generation',
  'dashboard.heroTitle': 'Generate Board Exam Questions Instantly 🎓',
  'dashboard.heroBody':
    'Create SSC / HSC exam papers powered by AI. Pick subject, chapters & difficulty — done in seconds.',
  'dashboard.heroCta': 'Start Generating',
  'dashboard.statTotalQuestions': 'Total Questions',
  'dashboard.statThisMonth': '{count} generated this month',
  'dashboard.statRank': 'Leaderboard Rank',
  'dashboard.statRankSub': 'out of {count} users',

  // ─── Dashboard: analytics ──────────────────────────────────────────────────
  'dashboard.analyticsTitle': 'Questions by Subject',
  'dashboard.analyticsCount': '{count} subjects',
  'dashboard.analyticsEmptyTitle': 'No data yet',
  'dashboard.analyticsEmptyBody': 'Start generating questions to see analytics',
  'dashboard.bySubject': 'By Subject',
  'dashboard.noQuestionsYet': 'No questions generated yet.',
  'dashboard.topUsers': 'Top Users',
  'dashboard.seeAll': 'See all →',
  'dashboard.recentTitle': 'Recent Generations',
  'dashboard.recentViewAll': 'View all ({count}) →',
  'dashboard.rankOf': 'Rank #{rank} of {total}',
  'dashboard.rankUp': 'Generate to rank up!',
  'dashboard.keepGenerating': 'Keep generating!',

  // ─── Dashboard: history ────────────────────────────────────────────────────
  'dashboard.historyEmpty': 'No history yet. Generate your first exam!',
  'dashboard.questionsCount': '{count} questions',

  // ─── Dashboard: subscription tab ───────────────────────────────────────────
  'subscription.signInPrompt': 'Sign in to manage your subscription.',
  'subscription.loadError': 'Could not load subscription.',
  'subscription.planLabel': '{plan} Plan',
  'subscription.daysLeft': '{days}d left',
  'subscription.daysShort': '{days}d',
  'subscription.pdfsUsed': 'PDFs used',
  'subscription.expiryWarning': 'Expires in {days} days. Renew soon.',
  'subscription.expiryWarningOne': 'Expires in 1 day. Renew soon.',
  'subscription.viewUpgrade': 'View / Upgrade Plans',
  'subscription.cancelAction': 'Cancel Subscription',
  'subscription.cancelling': 'Cancelling…',
  'subscription.cancelFailed': 'Failed to cancel. Please try again.',
  'subscription.confirmTitle': 'Cancel your subscription?',
  'subscription.confirmBody':
    'You keep access until the current period ends. You will not be charged again.',
  'subscription.confirmYes': 'Cancel subscription',
  'subscription.confirmNo': 'Keep it',
  'subscription.limitsTitle': 'Your plan limits',
  'subscription.maxWritten': 'Max written questions/PDF:',
  'subscription.maxMcq': 'Max MCQ questions/PDF:',
  'subscription.pdfsRemaining': 'PDFs remaining this month:',

  // ─── Dashboard: payments tab ───────────────────────────────────────────────
  'payments.signInPrompt': 'Sign in to see your payments.',
  'payments.emptyTitle': 'No payments yet',
  'payments.emptyBody': 'Your purchases will show up here once you upgrade.',
  'payments.viewPlans': 'View Plans',

  // ─── Pricing ───────────────────────────────────────────────────────────────
  'pricing.badge': 'Subscription Plans',
  'pricing.title': 'Choose your plan',
  'pricing.subtitle':
    'Generate SSC & HSC exam papers with AI. All plans include Bangla & English support.',
  'pricing.perMonth': 'per month',
  'pricing.days14': '14 days',
  'pricing.contactUs': 'contact us',
  'pricing.custom': 'Custom',
  'pricing.badgeFree': '14 Days Free',
  'pricing.badgeBest': 'Best Value',
  'pricing.planFreeTrial': 'Free Trial',
  'pricing.planProTrial': 'Pro Trial',
  'pricing.planPro': 'Pro',
  'pricing.planInstitution': 'Institution',
  'pricing.pdfsFree': '1 PDF',
  'pricing.pdfsProTrial': '20 PDFs/month',
  'pricing.pdfsPro': '40 PDFs/month',
  'pricing.pdfsInstitution': 'Custom volume',
  'pricing.ctaFreeTrial': 'Start Free Trial',
  'pricing.ctaProTrial': 'Get Pro Trial',
  'pricing.ctaPro': 'Get Pro',
  'pricing.ctaInstitution': 'Contact Developer',
  'pricing.featPdfsPerTrial': '1 PDF per trial',
  'pricing.featPdfsPerMonth': '{count} PDFs per month',
  'pricing.featMaxWritten': 'Max {count} written questions/PDF',
  'pricing.featMaxMcq': 'Max {count} MCQ questions/PDF',
  'pricing.featAllSubjects': 'All subjects & chapters',
  'pricing.featPdfDownload': 'PDF download',
  'pricing.featPdfImageDownload': 'PDF & image download',
  'pricing.featHistory': 'Question history',
  'pricing.featPriority': 'Priority AI generation',
  'pricing.featCustomVolume': 'Custom PDF volume',
  'pricing.featCustomLimits': 'Custom question limits',
  'pricing.featMultiUser': 'Multi-user access',
  'pricing.featSupport': 'Dedicated support',
  'pricing.featBranding': 'Custom branding',
  'pricing.featInvoice': 'Invoice billing',
  'pricing.payFailed': 'Payment failed. Please try again.',

  // ─── Payment result ────────────────────────────────────────────────────────
  'payment.successTitle': 'Payment Successful!',
  'payment.successBody':
    'Your subscription is now active. Start generating exam papers right away.',
  'payment.plan': 'Plan',
  'payment.pdfsPerMonth': 'PDFs/month',
  'payment.expires': 'Expires',
  'payment.activating': 'Activating subscription…',
  'payment.tranId': 'Transaction ID: {id}',
  'payment.goToDashboard': 'Go to Dashboard',
  'payment.receipt': 'A receipt has been sent to your registered email address.',
  'payment.failTitle': 'Payment Failed',
  'payment.failBody': 'Your payment could not be processed. No charge was made.',
  'payment.tryAgain': 'Try Again',
  'payment.backToDashboard': 'Back to Dashboard',
  'payment.cancelTitle': 'Payment Cancelled',
  'payment.cancelBody': 'You cancelled the payment. Your current plan remains unchanged.',
  'payment.viewPlans': 'View Plans',

  // ─── Wizard: chrome ────────────────────────────────────────────────────────
  'wizard.backToOverview': 'Back to Overview',
  'wizard.stepClass': 'Select Level',
  'wizard.stepVersion': 'Version',
  'wizard.stepDepartment': 'Department',
  'wizard.stepSubject': 'Select Subject',
  'wizard.stepPaper': 'Select Paper',
  'wizard.stepChapter': 'Select Chapter',
  'wizard.stepExamType': 'Exam Type',
  'wizard.stepQuestions': 'Questions',
  'wizard.stepReview': 'Review',
  'wizard.stepGenerate': 'Generate',

  // ─── Wizard: class ─────────────────────────────────────────────────────────
  'wizard.classTitle': 'Select Level',
  'wizard.classSubtitle': 'Choose the examination level for which you want to generate questions.',
  'wizard.classSscFull': 'Secondary School Certificate',
  'wizard.classHscFull': 'Higher Secondary Certificate',
  'wizard.classSscRange': 'Class 9 – 10',
  'wizard.classHscRange': 'Class 11 – 12',

  // ─── Wizard: version ───────────────────────────────────────────────────────
  'wizard.versionTitle': 'Select Version',
  'wizard.versionSubtitle': 'Choose the medium in which questions will be generated.',
  'wizard.versionBangla': 'বাংলা ভার্সন',
  'wizard.versionBanglaSub': 'Bangla Medium',
  'wizard.versionEnglish': 'English Version',
  'wizard.versionEnglishSub': 'English Medium',

  // ─── Wizard: department ────────────────────────────────────────────────────
  'wizard.departmentTitle': 'Select Department',
  'wizard.departmentSubtitle': 'Choose your department for Class {class} — {level}.',
  'wizard.departmentSkipping': 'Skipping department selection…',
  'wizard.levelSsc': 'SSC (Secondary)',
  'wizard.levelHsc': 'HSC (Higher Secondary)',

  // ─── Wizard: subject / paper / chapter ─────────────────────────────────────
  'wizard.subjectTitle': 'Select a Subject',
  'wizard.subjectSubtitle': 'Choose the subject for which you want to generate exam questions.',
  'wizard.paperTitle': 'Select a Paper',
  'wizard.paperSubtitle': 'Choose the paper for {subject}.',
  'wizard.paperChapters': '{count} chapters',
  'wizard.chapterTitle': 'Select Chapters',
  'wizard.chapterSubtitle': 'Choose one or more chapters from {source}. You can select multiple.',
  'wizard.chapterPlaceholder': 'Select chapters…',
  'wizard.chapterSelected': '{count} chapters selected',
  'wizard.chapterSelectedOne': '1 chapter selected',
  'wizard.chapterSearch': 'Search chapters…',
  'wizard.chapterNone': 'No chapters found',
  'wizard.chapterSelectedCount': '{count} selected',

  // ─── Wizard: exam type ─────────────────────────────────────────────────────
  'wizard.examTypeTitle': 'Select Exam Type',
  'wizard.examTypeSubtitle': 'What kind of questions do you want to generate?',
  'wizard.examTypeWrittenDesc': 'Creative questions with ক খ গ ঘ parts',
  'wizard.examTypeMcqDesc': 'Multiple choice questions with 4 options',

  // ─── Wizard: questions & duration ──────────────────────────────────────────
  'wizard.questionsTitle': 'Questions & Duration',
  'wizard.questionsSubtitle': 'Set the number of questions, time allowed, and difficulty level.',
  'wizard.questionsNumberLabel': 'Number of Questions',
  'wizard.questionsWrittenHint': 'For written exams each question has ক, খ, গ, ঘ parts.',
  'wizard.questionsMcqHint': 'Each MCQ question carries 1 mark.',
  'wizard.questionsLimitWritten': 'Your plan allows a maximum of {max} written questions per PDF.',
  'wizard.questionsLimitMcq': 'Your plan allows a maximum of {max} MCQ questions per PDF.',
  'wizard.durationLabel': 'Duration',
  'wizard.durationHint': 'Use the +/− buttons or type a value directly.',
  'wizard.difficultyLabel': 'Difficulty Level',
  'wizard.difficultyEasy': 'Easy',
  'wizard.difficultyMedium': 'Medium',
  'wizard.difficultyHard': 'Hard',
  'wizard.difficultyMixed': 'Mixed',
  'wizard.difficultyEasyDesc': 'Basic recall & definitions',
  'wizard.difficultyMediumDesc': 'Application & explanation',
  'wizard.difficultyHardDesc': 'Analysis & higher-order thinking',
  'wizard.difficultyMixedDesc': 'Blend of all levels',

  // ─── Wizard: review ────────────────────────────────────────────────────────
  'wizard.reviewTitle': 'Review Your Selections',
  'wizard.reviewSubtitle': 'Make sure everything is correct before generating questions.',
  'wizard.reviewNext': 'Looks Good, Continue',
  'wizard.reviewCardTitle': 'Review your selections',
  'wizard.reviewCardSubtitle': 'Everything look good? Generate when ready.',
  'wizard.reviewClass': 'Class',
  'wizard.reviewDepartment': 'Department',
  'wizard.reviewSubject': 'Subject',
  'wizard.reviewPaper': 'Paper',
  'wizard.reviewChapters': 'Chapters',
  'wizard.reviewExamType': 'Exam Type',
  'wizard.reviewQuestions': 'Questions',
  'wizard.reviewDuration': 'Duration',
  'wizard.reviewTotalMarks': 'Total Marks',
  'wizard.reviewVersion': 'Version',
  'wizard.reviewClassSsc': 'SSC (Class 9–10)',
  'wizard.reviewClassHsc': 'HSC (Class 11–12)',

  // ─── Wizard: generate ──────────────────────────────────────────────────────
  'wizard.generateTitle': 'Ready to Generate!',
  'wizard.generateSubtitle':
    'Your AI-powered exam questions will be generated based on your selected settings.',
  'wizard.generateSummary': 'Summary',
  'wizard.generateCta': 'Generate Questions',
  'wizard.generating': 'Generating with AI…',
  'wizard.generateNote':
    'AI will generate curriculum-aligned questions for Bangladesh {level} board exam.',
  'wizard.generateNoTemplate': 'No template found for the selected subject and exam type.',
  'wizard.generateFailed': 'Failed to generate questions. Please try again.',

  // ─── Generated questions viewer ────────────────────────────────────────────
  'questions.count': '{count} Questions',
  'questions.aiGenerated': 'AI Generated',
  'questions.png': 'PNG',
  'questions.downloadPdf': 'Download PDF',
  'questions.banner': '{count} questions generated for {chapters}. Download as PDF or PNG.',
  'questions.answerKey': 'Answer Key',
  'questions.questionNumber': 'Question {number}',
  'questions.noPaper': 'No exam paper found. Please go back and generate one.',

  // ─── Admin ─────────────────────────────────────────────────────────────────
  'admin.searchUsers': 'Search by name or email',
  'admin.colUser': 'User',
  'admin.colPlan': 'Plan',
  'admin.colSignedUp': 'Signed up',
  'admin.colLastSeen': 'Last seen',
  'admin.colGenerations': 'Papers',
  'admin.roleBadge': 'Admin',
  'admin.viewActivity': 'View activity',
  'admin.colAccount': 'Account',
  'admin.accountOk': 'Active',
  'admin.bannedBadge': 'Suspended',
  'admin.bannedUntil': 'Until {date}',
  'admin.ban': 'Suspend',
  'admin.unban': 'Lift',
  'admin.banTitle': 'Suspend this account',
  'admin.banDescription':
    '{user} will be signed out and blocked from the API and from signing in again.',
  'admin.banDuration': 'Duration',
  'admin.banDays': '{days} days',
  'admin.banIndefinite': 'Indefinite',
  'admin.banReason': 'Reason',
  'admin.banReasonPlaceholder': 'e.g. Automated scraping of the generation endpoint',
  'admin.banReasonHint': 'Shown to the user on every attempt to use the app.',
  'admin.banConfirm': 'Suspend account',
  'admin.banFailed': 'Could not suspend that account.',
  'admin.unbanFailed': 'Could not lift that suspension.',
  'admin.noUsers': 'No users match that search.',
  'admin.noActivity': 'Nothing logged yet. Activity appears here as people use the product.',
  'admin.filterAll': 'All event types',
  'admin.filterClear': 'Clear filters',
  'admin.filteredToUser': 'Showing activity for one user only.',
  'admin.showing': 'Showing {from}–{to} of {total}',
  'admin.never': 'Never',
  'admin.statTotalUsers': 'Total Users',
  'admin.statNewUsers': 'New in {days} days',
  'admin.statActiveSubs': 'Active Subscriptions',
  'admin.statGenerations': 'Papers Generated',
  'admin.statRevenue': 'Revenue ({days}d)',
  'admin.perDayTitle': 'Papers per day',
  'admin.planDistTitle': 'Active plans',
  'admin.eventBreakdownTitle': 'Events by type',
  'admin.rangeDays': 'Last {days} days',
  'admin.noStats': 'Not enough data yet.',

  // ─── Admin: activity event labels ──────────────────────────────────────────
  'activity.user_signed_up': 'Signed up',
  'activity.user_signed_in': 'Signed in',
  'activity.question_generated': 'Generated a paper',
  'activity.generation_blocked': 'Generation blocked',
  'activity.generation_failed': 'Generation failed',
  'activity.payment_initiated': 'Started a payment',
  'activity.payment_completed': 'Payment completed',
  'activity.payment_failed': 'Payment failed',
  'activity.subscription_activated': 'Plan activated',
  'activity.subscription_cancelled': 'Plan cancelled',
  'activity.user_banned': 'Account suspended',
  'activity.user_unbanned': 'Suspension lifted',
} as const;
